'use client';

import { useState } from 'react';
import LiteYouTube from './LiteYouTube';
import { badgeLabel, formatClock, isoDurationSeconds, type GalleryVideo, type VideoSource } from './types';

const BADGE_CLASS: Record<VideoSource['kind'], string> = {
  official: 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100',
  independent: 'bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100',
  consultancy: 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100',
  sponsored: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  competitor: 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100',
};

function VideoCard({ video }: { video: GalleryVideo }) {
  const [playing, setPlaying] = useState(false);
  const [start, setStart] = useState(0);
  const published = new Date(video.uploadDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

  return (
    <li className="w-[85%] flex-shrink-0 snap-start sm:w-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <LiteYouTube
        id={video.id}
        title={video.title}
        playing={playing}
        start={start}
        onPlay={() => setPlaying(true)}
      />
      <div className="mt-3 space-y-1">
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE_CLASS[video.source.kind]}`}>
          {badgeLabel(video.source)}
        </span>
        <h3 className="font-semibold leading-snug text-gray-900 dark:text-gray-100">{video.title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {video.channel} · {formatClock(isoDurationSeconds(video.duration))} · {published}
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">{video.description}</p>
      </div>
      {video.moments && video.moments.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Key moments</p>
          <ul className="space-y-1">
            {video.moments.map((m) => (
              <li key={m.t}>
                <button
                  type="button"
                  onClick={() => {
                    setStart(m.t);
                    setPlaying(true);
                  }}
                  className="text-left text-sm text-[#2813e8] dark:text-blue-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2cc3d1] rounded"
                >
                  <span className="font-mono">[{formatClock(m.t)}]</span> {m.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        <a href={`https://www.youtube.com/watch?v=${video.id}`} target="_blank" rel="noopener noreferrer" className="underline">
          Watch on YouTube
        </a>{' '}
        · © {video.channel}
      </p>
    </li>
  );
}

/**
 * 2-4 lite YouTube cards. A grid on desktop, a horizontal scroll strip on
 * mobile (native scroll-snap, not a carousel). Pair with videoObjectJsonLd() from ./types
 * for structured data.
 */
export default function VideoGallery({ videos }: { videos: GalleryVideo[] }) {
  return (
    <ul className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0">
      {videos.map((v) => (
        <VideoCard key={v.id} video={v} />
      ))}
    </ul>
  );
}
